import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Icon from '@welldev-group/rcl-icon';
import IconButton from '@welldev-group/rcl-icon-button';
import { I18nText, Link } from '@welldev-group/react-comp-lib';
import { Menu } from '@welldev-group/rcl-menu';
import { TableRow, TableData } from '@welldev-group/rcl-table';
import routes from '../../../../../../shared/routes';
import httpClient from '../../../../../../shared/libraries/httpClient';
import WorkSpaceModal from '../../../../Workspace/WorkSpaceModal';

const WorkspaceTableRow = ({
  workspace,
  status,
  openConfirmationModalOfUpdatingWorkspace,
  onDeactivateWorkspaceClick,
  affiliateId,
  userAffiliateId,
  fetchWorkspaces,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [affiliates, setAffiliates] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [workspaceName, setWorkspaceName] = useState('');
  const [workspaceIdentifier, setWorkspaceIdentifier] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const onOutsideClick = () => {
    setIsVisible(false);
  };

  const DEACTIVATED_STATE = 'deactivated';

  const updateWorkspace = async () => {
    const params = {
      workspace: {
        name: workspaceName,
      },
    };
    const { data } = await httpClient.put(
      routes.workspace.update({
        id: selectedWorkspace.id,
        affiliate_id: affiliateId,
      }),
      params,
    );
    return data;
  };

  const fetchAffiliates = async () => {
    const { data } = await httpClient.get(routes.affiliates.list());
    setAffiliates(data.affiliates);
  };

  useEffect(() => {
    fetchAffiliates();
  }, []);

  useEffect(() => {
    if (selectedWorkspace) {
      setWorkspaceName(selectedWorkspace.name);
      setWorkspaceIdentifier(selectedWorkspace.identifier);
    }
  }, [selectedWorkspace]);

  return (
    <>
      <TableRow>
        <TableData>
          <div className="affiliate-workspace-list__name">{workspace.name}</div>
        </TableData>
        <TableData>
          <div className="affiliate-workspace-list__identifier">{workspace.identifier}</div>
        </TableData>
        <TableData align="center">
          <div className="col-grid col-bleed direction-row align-center justify-center">
            <span className="affiliate-workspace-list__url">{workspace.agentUrl}</span>
            <span className="affiliate-workspace-list__url">
              <Link
                href={workspace.agentUrl}
                type="icon-button"
                size="tiny"
                modifier="tertiary"
                openNewTab={true}
              >
                <Icon name="externalLink" color="tertiary" />
              </Link>
            </span>
          </div>
        </TableData>
        <TableData>
          {status === DEACTIVATED_STATE ? (
            <div className="affiliate-workspace-list__menu">
              <IconButton
                version="v2"
                isIconOnly={true}
                icon={<Icon name="powerOff" />}
                size="large"
                label={<I18nText id="user.actions.reactivate" />}
                disabled={true}
              />
            </div>
          ) : (
            <div className="affiliate-workspace-list__menu">
              <Menu
                className="affiliate-workspace-list__menu-dropdown"
                isVisible={isVisible}
                size="small"
                popOverDirection="bottom-right"
                onOutsideClick={onOutsideClick}
                label={
                  <IconButton
                    size="small"
                    color="tertiary"
                    label={<I18nText id="show_more.more" />}
                    labelPlacement="bottom"
                    onClick={() => {
                      setIsVisible(previousIsVisibleState => !previousIsVisibleState);
                    }}
                    icon={<Icon name="horizontalDots" />}
                  />
                }
              >
                <>
                  <span
                    className="affiliate-workspace-list__menu-item"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setModalOpen(true);
                      setSelectedWorkspace(workspace);
                    }}
                    onKeyDown={() => setSelectedWorkspace(workspace)}
                  >
                    <I18nText id="affiliate_three_sixty.affiliate.users.table.edit" />
                  </span>

                  <span
                    className="affiliate-workspace-list__menu-item"
                    role="button"
                    onClick={() => onDeactivateWorkspaceClick(workspace)}
                    onKeyDown={() => onDeactivateWorkspaceClick(workspace)}
                    tabIndex={0}
                  >
                    <I18nText id="affiliate_three_sixty.affiliate.users.table.deactivate" />
                  </span>
                </>
              </Menu>
            </div>
          )}
        </TableData>
      </TableRow>
      <WorkSpaceModal
        onModalClose={() => setModalOpen(false)}
        isVisible={modalOpen}
        title={<I18nText id="affiliate_three_sixty.affiliate.workspaces.update.header" />}
        userAffiliateId={userAffiliateId}
        affiliates={affiliates.map(affiliate => ({
          value: affiliate.id,
          label: affiliate.clientName,
        }))}
        createWorkspace={updateWorkspace}
        fetchWorkspaces={fetchWorkspaces}
        openConfirmationModalOfCreatingWorkspace={() =>
          openConfirmationModalOfUpdatingWorkspace(workspace)
        }
        isSuperAdmin={false}
        action={updateWorkspace}
        subheaderText={
          <I18nText id="affiliate_three_sixty.affiliate.workspaces.update.subheader" />
        }
        selectedWorkspace={selectedWorkspace}
        workspaceName={workspaceName}
        setWorkspaceName={setWorkspaceName}
        workspaceIdentifier={workspaceIdentifier}
        setWorkspaceIdentifier={setWorkspaceIdentifier}
        disableIdentifierField={true}
        buttonText={<I18nText id="affiliate_three_sixty.affiliate.button_label.update" />}
      />
    </>
  );
};

WorkspaceTableRow.propTypes = {
  workspace: PropTypes.shape({
    name: PropTypes.string.isRequired,
    agentUrl: PropTypes.string.isRequired,
    identifier: PropTypes.string.isRequired,
    acronym: PropTypes.string.isRequired,
    updatedAt: PropTypes.string.isRequired,
    createdAt: PropTypes.string.isRequired,
    currency: PropTypes.string.isRequired,
  }).isRequired,
  fetchWorkspaces: PropTypes.func.isRequired,
  openConfirmationModalOfUpdatingWorkspace: PropTypes.func.isRequired,
  onDeactivateWorkspaceClick: PropTypes.func.isRequired,
  status: PropTypes.string.isRequired,
  userAffiliateId: PropTypes.number.isRequired,
  affiliateId: PropTypes.number.isRequired,
};

export default WorkspaceTableRow;
